<template>
    <Dialog :visible="showModal" modal @update:visible="v => emit('update:showModal', v)"
        :header="$t('prizes.selections.title')" class="w-full max-w-2xl">
        <div class="space-y-4">
            <p class="text-gray-600">{{ $t("prizes.selections.description") }}</p>

            <div>
                <FloatLabel variant="in" fluid>
                    <MultiSelect v-model="selectedSuggestions" :options="availableSuggestions" filter
                        optionLabel="description" :maxSelectedLabels="3" :selectionLimit="3" showClear fluid
                        id="prizeSelections" />
                    <label for="prizeSelections">{{ $t("prizes.selections.selectPrizes") }}</label>
                </FloatLabel>
            </div>

            <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600">
                    {{ $t("prizes.selections.selected", { count: selectedSuggestions.length }) }}
                </span>
                <div class="flex gap-3">
                    <Button type="button" severity="secondary" @click="cancelSelections">
                        {{ $t("prizes.selections.cancel") }}
                    </Button>
                    <Button @click="saveSelections" :disabled="selectedSuggestions.length === 0 || saving">
                        {{ $t("prizes.selections.save") }}
                    </Button>
                </div>
            </div>
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePrizeSettingsStore, usePrizesStore } from '@/stores';

interface Props {
    showModal: boolean;
}

interface Emits {
    (e: 'update:showModal', value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const prizeSettingsStore = usePrizeSettingsStore();
const prizesStore = usePrizesStore();

const saving = ref(false);
const selectedSuggestions = ref<any[]>([]);

const availableSuggestions = computed(() => prizeSettingsStore.prizeSuggestions);
const currentMonthSelections = computed(() => prizesStore.currentMonthSelections);

// Watch for opening modal to reset selections
watch(() => props.showModal, (isOpen) => {
    if (isOpen) {
        const currentSelectionIds = currentMonthSelections.value.map(s => s.prizeSuggestionId!);
        selectedSuggestions.value = availableSuggestions.value.filter(s =>
            currentSelectionIds.includes(s.id!)
        );
    }
});

function closeModal() {
    emit('update:showModal', false);
}

async function saveSelections() {
    saving.value = true;
    try {
        await prizesStore.setMonthlySelections({
            prizeSuggestionIds: selectedSuggestions.value.map(s => s.id!)
        });
        closeModal();
    } catch (error) {
        console.error('Failed to save selections:', error);
    } finally {
        saving.value = false;
    }
}

function cancelSelections() {
    selectedSuggestions.value = [];
    closeModal();
}
</script>
